import { calculateBMR, getActivityMultiplier, getGoalFactor } from './nutritionMath';

// Where each number on the Create Meal Plan page comes from, so the UI can say so.
//   saved      — the client's stored target (what the coach prescribed)
//   estimated  — computed from the client's profile + latest weight + goal
//   maintenance— computed maintenance (TDEE) because the goal is unknown
//   default    — a generic split the coach should review
//   missing    — nothing usable; the coach has to enter it

export const parseGrams = (value) => {
  if (value == null || value === '') return 0;
  const n = parseFloat(String(value).replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? Math.round(n) : 0;
};

const ageFrom = (client) => {
  if (client?.age) return Number(client.age);
  if (!client?.date_of_birth) return null;
  const b = new Date(client.date_of_birth);
  if (Number.isNaN(b.getTime())) return null;
  const now = new Date();
  let a = now.getFullYear() - b.getFullYear();
  if (now.getMonth() < b.getMonth() || (now.getMonth() === b.getMonth() && now.getDate() < b.getDate())) a -= 1;
  return a;
};

// Latest weigh-in wins; profile weight is the fallback.
export function resolveWeight(client, weightLogs) {
  const logs = (Array.isArray(weightLogs) ? weightLogs : [])
    .filter((l) => l && Number(l.weight_kg) > 0 && l.measurement_date)
    .sort((a, b) => new Date(b.measurement_date) - new Date(a.measurement_date));
  if (logs.length) return { kg: Number(logs[0].weight_kg), source: 'weighIn', date: logs[0].measurement_date };
  const profile = Number(client?.weight_kg ?? client?.weight);
  if (profile > 0) return { kg: profile, source: 'profile', date: null };
  return { kg: null, source: null, date: null };
}

export function resolveTargets({ targets = {}, client = {}, weightLogs = [] }) {
  const weight = resolveWeight(client, weightLogs);
  const goal = targets.goal || client.goal || null;

  const bmr = calculateBMR(ageFrom(client), client.gender, weight.kg, client.height_cm ?? client.height);
  const activity = client.Activity_level || client.activity_level || 'sedentary';
  const maintenance = bmr ? Math.round(bmr * getActivityMultiplier(activity)) : null;

  let calories = { value: null, source: 'missing' };
  const saved = parseInt(targets.calories, 10);
  if (saved > 0) calories = { value: saved, source: 'saved' };
  else if (maintenance && goal) calories = { value: Math.round(maintenance * getGoalFactor(goal)), source: 'estimated' };
  else if (maintenance) calories = { value: maintenance, source: 'maintenance' };

  const p = parseGrams(targets.protein);
  const c = parseGrams(targets.carbs);
  const f = parseGrams(targets.fats ?? targets.fat);
  const macrosSaved = p > 0 && c > 0 && f > 0;

  return { calories, macrosSource: macrosSaved ? 'saved' : 'default', savedMacros: { protein: p, carbs: c, fats: f }, weight, goal, maintenance };
}

// Day ±5%, meal ±10% (coach-confirmed tolerances).
export const DAY_TOLERANCE = 0.05;
export const MEAL_TOLERANCE = 0.10;

export function reconcile(actual, target, tolerance) {
  const a = Number(actual) || 0;
  const t = Number(target) || 0;
  if (t <= 0) return { delta: 0, status: 'none' };
  const delta = a - t;
  if (Math.abs(delta) <= t * tolerance) return { delta, status: 'on' };
  return { delta, status: delta > 0 ? 'over' : 'under' };
}

// meal_plan_structure comes in three shapes (the meal-plan service writes back whichever is stored):
//   [ {...}, ... ]                 — what this app writes
//   { meals: [ {...}, ... ] }      — wrapped list
//   { Breakfast: {...}, ... }      — legacy, keyed by slot name
// Also tolerates a JSON string of any of these. Always returns an array of slot objects.
export function toStructureArray(raw) {
  let value = raw;
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch { return []; }
  }
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    const inner = value.structure ?? value.meals ?? value.slots ?? value.meal_plan_structure;
    if (inner !== undefined && inner !== value) return toStructureArray(inner);
    return Object.entries(value)
      .filter(([, slot]) => slot && typeof slot === 'object' && !Array.isArray(slot))
      .map(([name, slot]) => (slot.mealSlot || slot.meal ? slot : { ...slot, meal: name }));
  }
  return [];
}

// Daily targets count as the client's prescription only when every number was saved.
export function hasSavedDailyTargets(targets = {}) {
  const resolved = resolveTargets({ targets });
  return resolved.calories.source === 'saved' && resolved.macrosSource === 'saved';
}

const numOrNull = (value) => {
  if (value == null || value === '') return null;
  const n = Number(String(value).replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : null;
};

// Stored slots come in two formats, and each is written back in the format it was read in:
//   onboarding ("legacy") — { meal, description, calories, calories_pct }: calories only, no per-meal macros
//   structured            — { mealSlot, mealName, targetCalories, targetMacros: { protein, carbs, fats } }
// Values are taken exactly as stored; nothing here rescales a slot to make the day add up.
//   mealSlot  — the slot (Breakfast, Lunch, …)
//   mealName  — the dish hint
// A slot without targetMacros keeps macros null; the page shows an "≈" estimate (estimateSlotMacros), never saves one.
export function readSlot(entry, dayCalories = 0) {
  const e = entry && typeof entry === 'object' ? entry : {};
  const pct = numOrNull(e.calories_pct);
  let calories = numOrNull(e.targetCalories ?? e.calories);
  if (calories == null && pct != null && dayCalories > 0) calories = Math.round((pct / 100) * dayCalories);
  const m = e.targetMacros && typeof e.targetMacros === 'object' ? e.targetMacros : null;
  const structured = e.mealSlot != null || e.targetCalories != null || !!m;
  return {
    mealSlot: String(e.mealSlot ?? e.meal ?? e.name ?? '').trim(),
    mealName: String(e.mealName ?? e.description ?? ''),
    targetCalories: calories ?? 0,
    targetMacros: m
      ? { protein: numOrNull(m.protein) ?? 0, carbs: numOrNull(m.carbs) ?? 0, fats: numOrNull(m.fats ?? m.fat) ?? 0 }
      : null,
    format: structured ? 'structured' : 'legacy',
  };
}

export const readSlots = (raw, dayCalories = 0) => toStructureArray(raw).map((entry) => readSlot(entry, dayCalories));

// Page slot → its stored format. dayCalories recomputes calories_pct for onboarding-format slots.
export function writeSlot(slot, dayCalories = 0) {
  const calories = Math.max(0, Math.round(Number(slot.targetCalories) || 0));
  if (slot.format === 'legacy') {
    return {
      meal: slot.mealSlot,
      description: slot.mealName || '',
      calories,
      calories_pct: dayCalories > 0 ? Math.round((calories / dayCalories) * 100) : 0,
    };
  }
  const out = { mealName: slot.mealName || '', mealSlot: slot.mealSlot, targetCalories: calories };
  if (slot.targetMacros) {
    out.targetMacros = {
      protein: Math.max(0, Math.round(Number(slot.targetMacros.protein) || 0)),
      carbs: Math.max(0, Math.round(Number(slot.targetMacros.carbs) || 0)),
      fats: Math.max(0, Math.round(Number(slot.targetMacros.fats) || 0)),
    };
  }
  return out;
}

// A slot's macros: saved ones exactly, otherwise its share of the day by calories (the same split the meal-plan
// service uses for onboarding-format slots). estimated tells the page to show "≈".
export function slotMacros(slot, day) {
  if (slot?.targetMacros) return { ...slot.targetMacros, estimated: false };
  const dayKcal = Number(day?.calories) || 0;
  const share = dayKcal > 0 ? (Number(slot?.targetCalories) || 0) / dayKcal : 0;
  return {
    protein: Math.round((Number(day?.protein) || 0) * share),
    carbs: Math.round((Number(day?.carbs) || 0) * share),
    fats: Math.round((Number(day?.fats) || 0) * share),
    estimated: true,
  };
}

export const sumSlotCalories = (slots) => (slots || []).reduce((sum, s) => sum + (Number(s.targetCalories) || 0), 0);

// Default slot names for a meal count, used when a client has a meal count but no saved slots.
export const DEFAULT_SLOTS_BY_COUNT = {
  1: ['Lunch'],
  2: ['Breakfast', 'Dinner'],
  3: ['Breakfast', 'Lunch', 'Dinner'],
  4: ['Breakfast', 'Lunch', 'Afternoon Snack', 'Dinner'],
  5: ['Breakfast', 'Morning Snack', 'Lunch', 'Afternoon Snack', 'Dinner'],
  6: ['Breakfast', 'Morning Snack', 'Lunch', 'Afternoon Snack', 'Dinner', 'Evening Snack'],
  7: ['Breakfast', 'Morning Snack', 'Lunch', 'Afternoon Snack', 'Pre-Workout Meal', 'Dinner', 'Evening Snack'],
  8: ['Pre-Morning Snack', 'Breakfast', 'Morning Snack', 'Lunch', 'Afternoon Snack', 'Pre-Workout Meal', 'Dinner', 'Evening Snack'],
};
export const defaultSlotNames = (count) => DEFAULT_SLOTS_BY_COUNT[Math.min(8, Math.max(1, Number(count) || 0))] || [];
